/* eslint-disable prettier/prettier */
import React, { useCallback, useEffect, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { useHistory } from 'react-router-dom';

import { ENV } from '@environment';
import * as Sentry from '@sentry/react';
import { MyThunkDispatch, RootState } from 'store';

import { autosFilterClear, autosSearchRequest } from 'action-creators/autos';
import { accountFailure, accountRequest } from 'action-creators/user';
import { autoResults, searchAutos, getFilters } from 'actions/autos';
import { updateAccount } from 'actions/user';
import useIsMobile from 'hooks/useIsMobile';
import useStoreTranslation from 'hooks/useStoreTranslation';
import useToast from 'hooks/useToast';
import useUserAccount from 'hooks/useUserAccount';
import { VehicleFilterResponse } from 'models/auto/vehicleFilter';
import AutosList from 'screens/components/AutosList';
import { amplitudelogEvent, ViewTravelResultsScreenEvent } from 'services/amplitudeService';
import { fetchAccount } from 'services/userService';
import { amplitudeEvents } from 'utils/amplitudeEvents';
import { daysCount, extractErrorMessage } from 'utils/generic';

const AutosListContainer = () => {
  const history = useHistory();
  const dispatch = useDispatch<MyThunkDispatch>();
  const {
    pickUpPoint,
    pickUpDate,
    pickUpTime,
    dropOffPoint,
    dropOffDate,
    dropOffTime,
    search,
    results,
    isLoadingResults,
    diffDropOff,
    traveler,
    lastTime,
    autoTabFilter,
    branchCompany,
  } = useSelector((state: RootState) => state.autos);
  const [loadingSearch, setLoadingSearch] = useState(false);
  const [availableFilters, setAvailableFilters] = useState<VehicleFilterResponse>();
  const { t } = useStoreTranslation();
  const toast = useToast();
  const user = useUserAccount();
  const [searchId, setSearchId] = useState<string>();
  const isMobile = useIsMobile();

  const handleAutoSearch = useCallback(async () => {
    if (pickUpPoint && dropOffPoint && pickUpDate && dropOffDate && pickUpTime && dropOffTime) {
      dispatch(accountRequest());
      const {
        data: { data },
      } = await fetchAccount();

      if (data.sessionToken === '' || data.sessionToken === undefined || data.sessionToken === null || data.isOperator) {
        dispatch(accountFailure('sessionToken is empty'));
        history.replace('/login');
      }

      // TODO - Debe ser por algun metodo o de una manera mas eficiente
      data.companyId = user.companyId;
      data.companyName = user.companyName;
      // TODO - Debe ser por algun metodo o de una manera mas eficiente Fin
      
      updateAccount(dispatch, data);
      const search = {
        pickUpPoint,
        dropOffPoint,
        pickUpDate,
        pickUpTime,
        dropOffDate,
        dropOffTime,
        diffDropOff,
        newSessionToken: data.sessionToken,
        traveler,
        branchCompany,
      };
      dispatch(autosFilterClear());
      dispatch(autosSearchRequest(search));
      return await dispatch(searchAutos(search));
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    diffDropOff,
    dispatch,
    dropOffDate,
    dropOffPoint,
    dropOffTime,
    pickUpDate,
    pickUpPoint,
    pickUpTime,
    traveler,
    branchCompany,
  ]);

  const handleAutoFilter = useCallback(async () => {
    try {
      const data: VehicleFilterResponse = await dispatch<any>(getFilters());
      setAvailableFilters(data);
    } catch (e) {
      toast.add({ type: 'danger', message: t('filters.fetchError') });
    }
  }, [dispatch, t, toast]);
  const handleAutoResults = useCallback(
    async (searchId: string) => await dispatch(autoResults(undefined, searchId)),
    [dispatch],
  );

  useEffect(() => {
    const handleSearch = async () => {
      const now = new Date();
      const before = lastTime ? new Date(lastTime) : null;
      const hasExpired = before ? before < now : true;

      if (
        (hasExpired || search === null || search === undefined) &&
        (results === null || results === undefined || results.length === 0) &&
        !isLoadingResults &&
        !loadingSearch
      ) {
        try {
          setLoadingSearch(true);
          const res: any = await handleAutoSearch();

          if (res.resultsCount === 0) {
            history.replace('/corporate/booking/autos', { showNoResults: true });
            return;
          }

          if (res.resultsCount > 0) {
            //Amplitude
            const distanceRestriction = res.distanceRestriction ?? search?.distanceRestriction;

            const event: ViewTravelResultsScreenEvent = {
              SOURCE: 'TRAVEL_HOME',
              RESULTS: res?.resultsCount ?? 0,
              TRAVEL_PRODUCT_TYPE: 'CAR_RENTAL',
              TRAVEL_FROM: pickUpPoint?.code ?? '',
              TRAVEL_TO: dropOffPoint?.code ?? '',
              TRAVEL_DESTINATION_TYPE: distanceRestriction
                ? distanceRestriction === 'Domestic'
                  ? 'NATIONAL'
                  : 'INTERNATIONAL'
                : '',
              TRAVEL_TRIP_TYPE: '',
              TRAVEL_ADVANCED_PURCHASE: pickUpDate ? daysCount(new Date(), new Date(pickUpDate)).toString() : '',
              TRAVEL_DATE_FROM: pickUpDate?.toString() ?? '',
              TRAVEL_DATE_TO: dropOffDate?.toString() ?? '',
              TRAVEL_TRIP_LENGTH:
                pickUpDate && dropOffDate ? daysCount(new Date(pickUpDate), new Date(dropOffDate)).toString() : '',
              TRAVEL_PASSENGERS: '1',
              TRAVEL_BRANCH_CODE: ENV.RAPPI_BRANCH_CODE ?? '',
              SEARCHID: search?.searchId ?? '',
            };

            amplitudelogEvent(amplitudeEvents.VIEW_TRAVEL_RESULTS_SCREEN, event, user);

            setSearchId(res.searchId);
            await Promise.all([handleAutoResults(res.searchId)]);
          }
        } catch (error) {
          const message = extractErrorMessage(error) || t('defaultError.default');

          if (message?.startsWith('Currency reported is different')) {
            Sentry.captureMessage('[Autos] Currency reported is different');
            toast.add({ type: 'danger', message: t('flightsList.error.differentCurrency') });
            history.replace('/corporate/booking/autos');
            return;
          }

          if (message?.toLowerCase().includes('cannot search for autos dates before')) {
            Sentry.captureMessage(`[Autos] ${message}`);
            history.replace('/corporate/booking/autos', { showNoResults: true });
            return;
          }

          const response = error.request?.response ? `[${error.request?.response}]` : '';
          Sentry.captureMessage(`[Autos] ${message} ${response}`);
          //alert(message);
          //toast.add({ type: 'danger', message: message });
          history.replace('/corporate/booking/autos', { showNoResults: true });
        } finally {
          setLoadingSearch(false);
        }
      }
      if (searchId !== undefined && (availableFilters === null || availableFilters === undefined)) {
        try {
          setLoadingSearch(true);
          await handleAutoFilter();
          setLoadingSearch(false);
        }
        catch(error) {
          const message = extractErrorMessage(error);
          toast.add({ type: 'danger', message: message || t('defaultError.default') });
          history.replace('/corporate/booking/hotels', { showNoResults: true });
        }
      }
    };

    if (pickUpPoint && dropOffPoint && pickUpDate && dropOffDate && pickUpTime && dropOffTime) {
      handleSearch();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    diffDropOff,
    dropOffDate,
    dropOffPoint,
    pickUpDate,
    pickUpPoint,
    pickUpTime,
    dropOffTime,
    lastTime,
    search,
    isLoadingResults,
  ]);
  const handleRequest = useCallback(() => dispatch(autoResults(undefined, searchId)), [dispatch, searchId]);

  return (
    <AutosList
      pickUpPoint={pickUpPoint}
      dropOffPoint={dropOffPoint}
      pickUpDate={pickUpDate ? new Date(pickUpDate) : undefined}
      dropOffDate={dropOffDate ? new Date(dropOffDate) : undefined}
      isLoading={loadingSearch}
      isLoadingResults={isLoadingResults}
      onRequest={handleRequest}
      autosResult={results || []}
      availableFilters={
        availableFilters || autoTabFilter ? ({ filter: autoTabFilter } as VehicleFilterResponse) : undefined
      }
      traveler={traveler}
    />
  );
};
export default AutosListContainer;
